import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { untilDestroyed } from '@ngneat/until-destroy';

export enum DataViewType {
  list = 'LIST',
  grid = 'GRID',
}

/**
 * Class to manage data source of virtual scroll.
 * In your component, create a new instance of IosDataSource, passing the behavior subject.
 * The behavior subject returns the "page" the cdk is seeing. Call your method to populate the list using that page.
 */
export class CustomDataSource<T> extends DataSource<T | undefined> {
  private _totalCount: number;
  private readonly _pageSize: number = 50;
  private _hasMore: boolean;
  private _cachedData: { [key: number]: T[] } = {};
  private _fetchedPages = new Set<number>();
  private _itemPerRow: number = 1;
  private _viewType: DataViewType = DataViewType.list;
  private readonly _myData: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private readonly _dataStream: BehaviorSubject<(T | undefined)[]> = new BehaviorSubject<(T | undefined)[]>([]);
  private _subscription = new Subscription();
  private update$: BehaviorSubject<number>;

  constructor(behaviorSubject: BehaviorSubject<number>, pageSize?: number) {
    super();
    this.update$ = behaviorSubject;
    if (pageSize) {
      this._pageSize = pageSize;
    }
  }

  get totalCount(): number {
    return this._totalCount;
  }

  set totalCount(totalCount: number) {
    this._totalCount = totalCount;
  }

  get hasMore(): boolean {
    return this._hasMore;
  }

  set hasMore(hasMore: boolean) {
    this._hasMore = hasMore;
  }

  set itemPerRow(itemPerRow: number) {
    this._itemPerRow = itemPerRow;
  }

  get viewType(): DataViewType {
    return this._viewType;
  }

  set viewType(viewType: DataViewType) {
    this._viewType = viewType;
  }

  get allData(): T[] {
    return this._myData.getValue();
  }

  destroy() {
    // Do not remove this method!
  }

  /**
   * Clear all the data source cache.
   */
  public clearCache() {
    this._cachedData = {};
    this._fetchedPages = new Set<number>();
    this._myData.next([]);
    this._hasMore = true;
  }

  /**
   * Re-chunk data when visualization is grid
   */
  rebuildDataGrid() {
    if (this._viewType == DataViewType.grid) {
      const chunkItems = this._myData.getValue().reduce((chunks: T[][], item: T, index) => {
        const chunk = Math.floor(index / this._itemPerRow);
        chunks[chunk] = ([] as T[]).concat(chunks[chunk] || [], item);
        return chunks;
      }, []);

      this._dataStream.next(chunkItems);
    }
  }

  /**
   * Add new page to cached elements.
   * Call this method after the elements of a new page are received from server.
   * @param {T[]} elements
   * @param {number} page
   */
  addPageCache(elements: T[], page: number) {
    this._cachedData[page] = elements;
    const tmp: T[] = [];
    for (const elem in this._cachedData) {
      tmp.push(...this._cachedData[elem]);
    }
    this._fetchedPages.add(page);
    this._myData.next(tmp);
  }

  /**
   * Update the virtual scroll data source.
   * @param {T[]} elements
   */
  updateDataSource(elements: T[]) {
    this._myData.next(elements);
  }

  connect(collectionViewer: CollectionViewer): Observable<(T | undefined)[]> {
    if (this._subscription.closed) {
      this._subscription = new Subscription();
    }
    this._subscription.add(
      collectionViewer.viewChange.pipe(untilDestroyed(this, 'destroy')).subscribe((range) => {
        const startPage = this._getPageForIndex(range.start);
        const endPage = this._getPageForIndex(range.end);
        for (let i = startPage; i <= endPage; i++) {
          this._fetchPage(i);
        }
      }),
    );

    this._myData.pipe(untilDestroyed(this, 'destroy')).subscribe({
      next: (data) => {
        if (this._viewType == DataViewType.grid) {
          this.rebuildDataGrid();
        } else {
          this._dataStream.next(data);
        }
      },
    });

    return this._dataStream;
  }

  disconnect(): void {
    this._subscription.unsubscribe();
  }

  private _getPageForIndex(index: number): number {
    return Math.floor(index / (this._pageSize / this._itemPerRow));
  }

  private _fetchPage(page: number) {
    if (this._fetchedPages.has(page) || !this._hasMore) {
      return;
    }
    this._fetchedPages.add(page);
    this.update$.next(page);
  }
}
